import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Building2, Ticket } from "lucide-react";
import EventCard from "@/components/site/EventCard";
import { API_BASE } from "@/lib/supabase";

export default function Organization() {
  const { slug } = useParams();
  const [org, setOrg] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const { data } = await axios.get(`${API_BASE}/organizations/${slug}`);
        if (!ignore) {
          setOrg(data.organization || null);
          setItems(data.items || []);
        }
      } catch (e) {
        if (!ignore) {
          if (e?.response?.status === 404) {
            setNotFound(true);
          } else {
            setError(e?.response?.data?.detail || e.message);
          }
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div data-testid="organization-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="animate-pulse flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-svj-card border border-svj-border" />
          <div className="h-8 w-48 bg-svj-card border border-svj-border rounded-full" />
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse bg-svj-card border border-svj-border rounded-2xl h-72" />
          ))}
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div data-testid="organization-not-found" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <Building2 className="h-10 w-10 text-svj-purple mx-auto" />
        <h1 className="mt-4 text-2xl font-bold text-white">Organisatie niet gevonden</h1>
        <p className="mt-2 text-slate-400">Deze organisatiepagina bestaat niet (meer).</p>
        <Link to="/events" className="mt-6 inline-flex items-center gap-2 text-svj-cyan hover:text-white text-sm">
          <ArrowLeft className="h-4 w-4" /> Alle events bekijken
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="organization-error" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="rounded-2xl border border-svj-border bg-svj-card p-8 text-slate-300">
          <div className="text-white font-semibold mb-2">Kon organisatie niet ophalen</div>
          <p className="text-sm text-slate-400">{String(error)}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="organization-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
      <Link
        to="/events"
        className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-6"
        data-testid="organization-back"
      >
        <ArrowLeft className="h-4 w-4" /> Alle events
      </Link>

      <div className="flex items-center gap-4">
        <div className="bg-white p-1 rounded-full shadow-lg w-16 h-16 flex items-center justify-center overflow-hidden shrink-0">
          {org?.logo_url ? (
            <img src={org.logo_url} alt={org.name} className="w-full h-full object-cover rounded-full" />
          ) : (
            <Building2 className="h-8 w-8 text-svj-purple" />
          )}
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">Organisatie</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
            {org?.name || "Organisatie"}
          </h1>
        </div>
      </div>

      <div className="mt-10">
        {items.length === 0 ? (
          <div
            data-testid="organization-empty"
            className="rounded-2xl border border-svj-border bg-svj-card p-10 text-center"
          >
            <Ticket className="h-8 w-8 text-svj-cyan mx-auto" />
            <div className="mt-3 text-white font-semibold">Geen events gevonden</div>
            <p className="mt-1 text-sm text-slate-400">
              Deze organisatie heeft op dit moment geen actieve events.
            </p>
          </div>
        ) : (
          <div
            data-testid="organization-events-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8"
          >
            {items.map((ev) => (
              <EventCard key={ev.id} ev={ev} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
